import React, { useEffect, useRef, useState } from 'react';
import { ThumbsUp, ThumbsDown, Copy, RefreshCw, Check, Loader2, Sparkles } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import { Button } from 'App/shadcn/ui/button';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Tooltip, TooltipTrigger, TooltipPortal } from 'App/shadcn/ui/tooltip';
import T from 'Components/Helpers/translate';
import AssistantResource, { type CompiledPlaybookDraft } from 'Resources/Assistant';
import WorkspacePlaybookDraft from 'App/shadcn/pages/AIWorkspace/WorkspacePlaybookDraft';
import { notifyAIWorkspaceError } from 'App/shadcn/pages/AIWorkspace/notifyAIWorkspaceError';

type Props = {
	messageIndex: number;
	conversationId: number | null;
	onSubmit: (
		messageIndex: number,
		type: 'up' | 'down',
		reason: string
	) => Promise<{ saved: boolean; savedValue?: string } | null> | void;
	content?: string;
	onRegenerate?: (messageIndex: number) => void | Promise<void>;
	disabled?: boolean;
	/**
	 * WEB-6857 — `agent_run.id` for this assistant message, read from
	 * `message.metadata.agentRunId`. Renders the "Compile to Playbook" button
	 * when set together with `playbooksEnabled`. Old messages from before the
	 * metadata stamping landed simply have no button.
	 */
	agentRunId?: number | null;
	/** Result of `usePlaybooksAccess()` from the parent. */
	playbooksEnabled?: boolean;
};

const copyToClipboard = async (text: string): Promise<boolean> => {
	if (!text) return false;
	try {
		if (navigator.clipboard && window.isSecureContext) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		/* fall through to execCommand fallback */
	}
	try {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.style.position = 'fixed';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.select();
		const ok = document.execCommand('copy');
		document.body.removeChild(ta);
		return ok;
	} catch {
		return false;
	}
};

// Shared dark tooltip content, PORTALED (via TooltipPortal → the shadow-scope
// container) so it isn't clipped by the chat thread's overflow (WEB-7757).
const TipContent = ({ children }: { children: React.ReactNode }) => (
	<TooltipPortal>
		<TooltipPrimitive.Content
			side="top"
			align="start"
			sideOffset={4}
			collisionPadding={8}
			style={{
				zIndex: 60,
				background: 'oklch(0.22 0.012 250)',
				color: '#fff',
				fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
				fontSize: 11,
				fontWeight: 500,
				lineHeight: 1.2,
				padding: '4px 8px',
				borderRadius: 6,
				boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.2)'
			}}
		>
			{children}
		</TooltipPrimitive.Content>
	</TooltipPortal>
);

const FeedbackInline = ({
	messageIndex,
	onSubmit,
	content,
	onRegenerate,
	disabled,
	agentRunId,
	playbooksEnabled
}: Props) => {
	const [selected, setSelected] = useState<'up' | 'down' | null>(null);
	const [showInput, setShowInput] = useState(false);
	const [reason, setReason] = useState('');
	const [submitted, setSubmitted] = useState<null | 'up' | 'down'>(null);
	const [savedMemory, setSavedMemory] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [copied, setCopied] = useState(false);
	const [regenerating, setRegenerating] = useState(false);
	const [compiling, setCompiling] = useState(false);
	const [playbookDraft, setPlaybookDraft] = useState<CompiledPlaybookDraft | null>(null);
	const inputFormRef = useRef<HTMLDivElement | null>(null);

	const handleCompilePlaybook = async () => {
		if (!agentRunId || compiling) return;
		setCompiling(true);
		try {
			const draft = await AssistantResource.compilePlaybookFromRun(agentRunId);
			setPlaybookDraft(draft);
		} catch (err) {
			notifyAIWorkspaceError(err as Error, 'compilePlaybookFromRun');
		} finally {
			setCompiling(false);
		}
	};

	useEffect(() => {
		if (showInput) {
			inputFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [showInput]);

	const handleCopy = async () => {
		if (!content) return;
		const ok = await copyToClipboard(content);
		if (ok) {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		}
	};

	const handleRegenerate = async () => {
		if (!onRegenerate || disabled || regenerating) return;
		try {
			setRegenerating(true);
			await onRegenerate(messageIndex);
		} finally {
			setRegenerating(false);
		}
	};

	const handleClick = (type: 'up' | 'down') => {
		setSelected(type);
		setShowInput(true);
		setReason('');
	};

	const handleSubmit = async () => {
		if (!reason.trim() || !selected || submitting) return;
		setSubmitting(true);
		try {
			const result = await onSubmit(messageIndex, selected, reason.trim());
			if (result && result.saved && result.savedValue) {
				setSavedMemory(result.savedValue);
			}
			setSubmitted(selected);
			setShowInput(false);
		} finally {
			setSubmitting(false);
		}
	};

	if (submitted) {
		const Icon = submitted === 'up' ? ThumbsUp : ThumbsDown;
		return (
			<div
				className="mt-1 flex items-center gap-1.5"
				style={{ color: 'var(--pr-ink-4)', fontSize: 12 }}
			>
				<Icon className="h-5 w-5 shrink-0" />
				{savedMemory ? (
					<span>
						{T('assistant.feedbackSavedMemory')}
						{': '}
						<span style={{ color: 'var(--pr-ink-2)' }}>{savedMemory}</span>
					</span>
				) : (
					<span>{T('assistant.feedbackThanks')}</span>
				)}
			</div>
		);
	}

	return (
		<div>
			<div className="pr-feedback pr-feedback-row mt-1">
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<button
							type="button"
							className={cn('pr-fbtn', selected === 'up' && 'active')}
							onClick={() => handleClick('up')}
						>
							<ThumbsUp className="h-5 w-5" />
						</button>
					</TooltipTrigger>
					<TipContent>
						{T('assistant.feedbackGood')}
					</TipContent>
				</Tooltip>
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<button
							type="button"
							className={cn('pr-fbtn', selected === 'down' && 'active')}
							onClick={() => handleClick('down')}
						>
							<ThumbsDown className="h-5 w-5" />
						</button>
					</TooltipTrigger>
					<TipContent>
						{T('assistant.feedbackBetter')}
					</TipContent>
				</Tooltip>
				<span className="pr-feedback-divider" />
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<button
							type="button"
							className={cn('pr-fbtn', copied && 'active')}
							onClick={handleCopy}
							disabled={!content}
						>
							{copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
						</button>
					</TooltipTrigger>
					<TipContent>
						{copied ? T('assistant.copied') : T('default.copy')}
					</TipContent>
				</Tooltip>
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<button
							type="button"
							className="pr-fbtn"
							onClick={handleRegenerate}
							disabled={!onRegenerate || disabled || regenerating}
						>
							<RefreshCw className={cn('h-5 w-5', regenerating && 'animate-spin')} />
						</button>
					</TooltipTrigger>
					<TipContent>
						{T('assistant.regenerate')}
					</TipContent>
				</Tooltip>
				{/* WEB-6857: Compile this reply's run into a Playbook. Gated by
				     AGENT_PLAYBOOKS soft-deploy + presence of a numeric agentRunId
				     on the message metadata (stamped in agentEngine since this
				     change; older messages just don't show the button). */}
				{playbooksEnabled && agentRunId ? (
					<Tooltip delayDuration={0}>
						<TooltipTrigger asChild>
							<button
								type="button"
								className={cn('pr-fbtn', compiling && 'active')}
								onClick={handleCompilePlaybook}
								disabled={compiling}
							>
								{compiling ? (
									<Loader2 className="h-5 w-5 animate-spin" />
								) : (
									<Sparkles className="h-5 w-5" />
								)}
							</button>
						</TooltipTrigger>
						<TipContent>
							{T('assistant.playbook.compileButton')}
						</TipContent>
					</Tooltip>
				) : null}
			</div>
			{showInput && (
				<div
					ref={inputFormRef}
					className="flex flex-col gap-3 mt-2"
					style={{ maxWidth: 420, scrollMarginBottom: 96 }}
				>
					<span style={{ fontSize: 12.5, color: 'var(--pr-ink-3)' }}>
						{selected === 'up' ? T('assistant.feedbackGood') : T('assistant.feedbackBetter')}
					</span>
					<input
						type="text"
						value={reason}
						onChange={e => setReason(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter' && reason.trim()) handleSubmit();
							if (e.key === 'Escape' && !submitting) {
								setShowInput(false);
								setSelected(null);
							}
						}}
						placeholder={T('assistant.feedbackPlaceholder')}
						autoFocus
						disabled={submitting}
						style={{
							border: 'none',
							borderBottom: '1px solid var(--pr-border)',
							background: 'transparent',
							outline: 'none',
							fontSize: 13,
							padding: '6px 2px',
							color: 'var(--pr-ink)',
							fontFamily: 'inherit',
							opacity: submitting ? 0.6 : 1
						}}
					/>
					<div className="flex items-center gap-3">
						<Button
							size="sm"
							onClick={handleSubmit}
							disabled={!reason.trim() || submitting}
							className="min-w-[92px]"
						>
							{submitting ? (
								<span className="flex items-center gap-1.5">
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
									{T('assistant.feedbackEvaluating') || 'Evaluating…'}
								</span>
							) : (
								T('assistant.submit')
							)}
						</Button>
						<Button
							size="sm"
							variant="ghost"
							className="text-grey-9"
							disabled={submitting}
							onClick={() => {
								setShowInput(false);
								setSelected(null);
							}}
						>
							{T('assistant.cancel')}
						</Button>
					</div>
				</div>
			)}
			{playbookDraft && (
				<WorkspacePlaybookDraft
					draft={playbookDraft}
					routineId={null}
					routineLabel={null}
					canSave
					onClose={() => setPlaybookDraft(null)}
					onSaved={() => setPlaybookDraft(null)}
				/>
			)}
		</div>
	);
};

export default FeedbackInline;
